import React, { useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  IconButton,
  Chip,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import MergeIcon from "@mui/icons-material/MergeType";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { PDFDocument } from "pdf-lib";
import PageHeader from "../../ui-components/PageHeader";

export default function PDFCombiner() {
  const [files, setFiles] = useState([null, null]); // [pdf1, pdf2]
  const [pageInfos, setPageInfos] = useState([null, null]); // [{pages, size}, ...]
  const [skipLast, setSkipLast] = useState([false, false]); // skip last page per file
  const [skipFirst, setSkipFirst] = useState([false, false]); // skip first page per file
  const [merging, setMerging] = useState(false);
  const [resultUrl, setResultUrl] = useState(null);
  const [resultName, setResultName] = useState("");
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState([false, false]); // drag hover state per slot
  const inputRefs = [useRef(), useRef()];

  // Read PDF and extract page count
  const loadPdfInfo = useCallback(async (file) => {
    const buffer = await file.arrayBuffer();
    const pdf = await PDFDocument.load(buffer, { ignoreEncryption: true });
    return {
      pages: pdf.getPageCount(),
      size: (file.size / 1024 / 1024).toFixed(1),
    };
  }, []);

  // Shared: process a file for a given slot
  const processFile = useCallback(
    async (index, file) => {
      if (!file) return;
      if (file.type !== "application/pdf" && !file.name?.toLowerCase().endsWith(".pdf")) {
        setError("Alleen PDF-bestanden zijn toegestaan.");
        return;
      }
      setError(null);
      setResultUrl(null);

      try {
        const info = await loadPdfInfo(file);
        setFiles((prev) => {
          const next = [...prev];
          next[index] = file;
          return next;
        });
        setPageInfos((prev) => {
          const next = [...prev];
          next[index] = info;
          return next;
        });
      } catch (err) {
        setError(`Kon PDF niet lezen: ${err.message}`);
      }
    },
    [loadPdfInfo]
  );

  const handleFileSelect = useCallback(
    (index, event) => {
      processFile(index, event.target.files?.[0]);
    },
    [processFile]
  );

  // Drag & drop handlers
  const handleDragOver = useCallback((index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, []);

  const handleDragLeave = useCallback((index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver((prev) => {
      const next = [...prev];
      next[index] = false;
      return next;
    });
  }, []);

  const handleDrop = useCallback(
    (index, e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver((prev) => {
        const next = [...prev];
        next[index] = false;
        return next;
      });
      const file = e.dataTransfer.files?.[0];
      processFile(index, file);
    },
    [processFile]
  );

  const removeFile = useCallback((index) => {
    setFiles((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    setPageInfos((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    setSkipLast((prev) => {
      const next = [...prev];
      next[index] = false;
      return next;
    });
    setSkipFirst((prev) => {
      const next = [...prev];
      next[index] = false;
      return next;
    });
    setResultUrl(null);
    if (inputRefs[index].current) inputRefs[index].current.value = "";
  }, []);

  const swapFiles = useCallback(() => {
    setFiles((prev) => [prev[1], prev[0]]);
    setPageInfos((prev) => [prev[1], prev[0]]);
    setSkipLast((prev) => [prev[1], prev[0]]);
    setSkipFirst((prev) => [prev[1], prev[0]]);
    setResultUrl(null);
    // Reset file inputs
    inputRefs.forEach((ref) => {
      if (ref.current) ref.current.value = "";
    });
  }, []);

  const handleMerge = useCallback(async () => {
    if (!files[0] || !files[1]) {
      setError("Upload beide PDF-bestanden.");
      return;
    }
    setMerging(true);
    setError(null);
    setResultUrl(null);

    try {
      const mergedPdf = await PDFDocument.create();

      for (let i = 0; i < 2; i++) {
        const buffer = await files[i].arrayBuffer();
        const srcPdf = await PDFDocument.load(buffer, {
          ignoreEncryption: true,
        });
        const totalPages = srcPdf.getPageCount();

        // Determine which pages to include
        let startPage = skipFirst[i] ? 1 : 0;
        let endPage = skipLast[i] ? totalPages - 1 : totalPages;

        // Safety: don't skip if it would result in 0 pages
        if (endPage <= startPage) {
          startPage = 0;
          endPage = totalPages;
        }

        const pageIndices = [];
        for (let p = startPage; p < endPage; p++) {
          pageIndices.push(p);
        }

        const copiedPages = await mergedPdf.copyPages(srcPdf, pageIndices);
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      const mergedBytes = await mergedPdf.save();
      const blob = new Blob([mergedBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      // Generate filename from input files
      const name1 = files[0].name.replace(/\.pdf$/i, "");
      const name2 = files[1].name.replace(/\.pdf$/i, "");
      const outputName = `${name1} + ${name2}.pdf`;

      setResultUrl(url);
      setResultName(outputName);
    } catch (err) {
      setError(`Fout bij samenvoegen: ${err.message}`);
    } finally {
      setMerging(false);
    }
  }, [files, skipLast, skipFirst]);

  const handleDownload = useCallback(() => {
    if (!resultUrl) return;
    const a = document.createElement("a");
    a.href = resultUrl;
    a.download = resultName;
    a.click();
  }, [resultUrl, resultName]);

  const labels = ["PDF 1 (eerst)", "PDF 2 (daarna)"];

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: "auto", px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
      <PageHeader
        icon="merge_type"
        title="PDF Combiner"
        subtitle="Twee PDF's samenvoegen, optioneel laatste of eerste pagina overslaan"
      />

      {/* Upload sections */}
      {[0, 1].map((index) => (
        <Paper
          key={index}
          elevation={dragOver[index] ? 6 : 2}
          onDragOver={(e) => handleDragOver(index, e)}
          onDragLeave={(e) => handleDragLeave(index, e)}
          onDrop={(e) => handleDrop(index, e)}
          sx={{
            p: 3,
            mb: index === 0 ? 1 : 3,
            borderRadius: 2,
            border: files[index] ? "2px solid" : "2px dashed",
            borderColor: dragOver[index]
              ? "primary.main"
              : files[index]
              ? "primary.main"
              : "divider",
            bgcolor: dragOver[index] ? "action.hover" : "background.paper",
            transition: "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: files[index] ? 2 : 0,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {labels[index]}
            </Typography>
            {files[index] && (
              <IconButton
                size="small"
                onClick={() => removeFile(index)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {!files[index] ? (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                cursor: "pointer",
              }}
              onClick={() => inputRefs[index].current?.click()}
            >
              <input
                ref={inputRefs[index]}
                type="file"
                accept=".pdf"
                hidden
                onChange={(e) => handleFileSelect(index, e)}
              />
              <UploadFileIcon
                sx={{ fontSize: 40, color: dragOver[index] ? "primary.main" : "text.disabled", mb: 1 }}
              />
              <Typography
                variant="body1"
                color={dragOver[index] ? "primary.main" : "text.secondary"}
                fontWeight={dragOver[index] ? 600 : 400}
              >
                {dragOver[index]
                  ? "Laat los om te uploaden"
                  : "Sleep een PDF hierheen of klik om te kiezen"}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <PictureAsPdfIcon color="error" />
                <Typography fontWeight={500}>{files[index].name}</Typography>
                <Chip
                  label={`${pageInfos[index]?.pages} pagina's`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`${pageInfos[index]?.size} MB`}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={skipLast[index]}
                      onChange={(e) =>
                        setSkipLast((prev) => {
                          const next = [...prev];
                          next[index] = e.target.checked;
                          return next;
                        })
                      }

                    />
                  }
                  label="Laatste pagina overslaan"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={skipFirst[index]}
                      onChange={(e) =>
                        setSkipFirst((prev) => {
                          const next = [...prev];
                          next[index] = e.target.checked;
                          return next;
                        })
                      }

                    />
                  }
                  label="Eerste pagina overslaan"
                />
              </Box>
            </>
          )}
        </Paper>
      ))}

      {/* Swap button between the two uploads */}
      {files[0] && files[1] && (
        <Box sx={{ display: "flex", justifyContent: "center", my: -1.5, mb: 2 }}>
          <Tooltip title="Volgorde omdraaien">
            <IconButton onClick={swapFiles} color="primary" size="small">
              <SwapVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Merge button */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        startIcon={<MergeIcon />}
        onClick={handleMerge}
        disabled={!files[0] || !files[1] || merging}
        sx={{ mb: 2, py: 1.5 }}
      >
        {merging ? "Samenvoegen..." : "PDF's samenvoegen"}
      </Button>

      {merging && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Result */}
      {resultUrl && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: "success.main",
            color: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Samengevoegd!
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {resultName}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                bgcolor: "white",
                color: "success.main",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              Download PDF
            </Button>
          </Box>
        </Paper>
      )}

      {/* Info box */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <strong>Tip:</strong> Heb je een contract waarvan de laatste pagina een
        handtekeningenpagina is? Upload het contract als PDF 1 met "Laatste
        pagina overslaan" aan, en upload de ingescande handtekeningenpagina als
        PDF 2.
      </Alert>
    </Box>
  );
}
